"use client";
/**
 * Câmpul anti-roboți al formularelor publice (login, recuperare parolă): imaginea
 * cu codul, butonul de reîmprospătare și inputul. Părintele ține răspunsul și
 * jetonul; după orice eroare de trimitere crește `reload` — jetonul e de unică
 * folosință, deci fiecare încercare are nevoie de o imagine nouă.
 *
 * FĂRĂ gard „busy” care să înghită apăsările: o cerere atârnată în rețea l-ar
 * bloca pe veci și butonul ar părea mort. În loc: contor de secvență (ultimul
 * răspuns câștigă) + timeout — orice apăsare pornește mereu o cerere nouă.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { I, SEL } from "@/components/ui/icons";
import { newCaptchaAction } from "@/app/login/actions";

const timeout = <T,>(p: Promise<T>, ms: number): Promise<T> =>
  Promise.race([p, new Promise<never>((_, rej) => setTimeout(() => rej(new Error("timeout")), ms))]);

export function CaptchaField({ code, onCode, onToken, reload }: {
  code: string;
  onCode: (v: string) => void;
  onToken: (t: string) => void;
  reload: number;
}) {
  const [img, setImg] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | undefined>(undefined); // doar pe dev, pentru e2e
  const [err, setErr] = useState("");
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    const id = ++seq.current;
    setImg(null);
    setErr("");
    onCode("");
    try {
      const r = await timeout(newCaptchaAction(), 12000);
      if (seq.current !== id) return; // între timp s-a cerut alta — răspunsul ăsta nu mai contează
      if ("error" in r) setErr(r.error);
      else { setImg(r.image); setDevCode(r.code); onToken(r.token); }
    } catch {
      if (seq.current === id) setErr("Imaginea nu s-a încărcat. Apasă pe săgeți ca să încerci din nou.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void refresh(); }, [refresh, reload]);

  return (
    <div>
      <div className="flex items-center gap-2">
        <div className="grid h-[50px] w-[158px] shrink-0 place-items-center overflow-hidden rounded-md border border-line bg-surface-2">
          {img ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={img}
              data-cod={devCode}
              alt="Cod de verificare"
              className="h-full w-full object-contain"
              draggable={false}
            />
          ) : (
            <span className="size-4 animate-spin rounded-full border-2 border-line-2 border-t-ink-3" />
          )}
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          title="Altă imagine"
          aria-label="Altă imagine"
          className="grid size-9 shrink-0 place-items-center rounded-md border border-line text-ink-3 transition hover:text-ink"
        >
          <I name="refresh" className="!size-4" />
        </button>
        <input
          value={code}
          onChange={(e) => onCode(e.target.value.replace(/[^0-9A-Za-z]/g, "").toUpperCase().slice(0, 5))}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="Codul din imagine"
          aria-label="Codul din imagine"
          className={`${SEL} w-full tracking-[0.18em]`}
        />
      </div>
      {err && <p className="mt-1.5 text-[11.5px] font-medium text-neg">{err}</p>}
    </div>
  );
}
